// Library Methods
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import TitleWithTooltip from "src/components/shared/text/TitleWithTooltip/TitleWithTooltip";
import ExamCardContainer from "../card-ui/ExamCardContainer/ExamCardContainer";
import ExamCardHeader from "../card-ui/ExamCardHeader/ExamCardHeader";
import ExamCardButton from "../card-ui/ExamCardButton/ExamCardButton";
import ExamCardToggles from "../card-ui/ExamCardToggles/ExamCardToggles";

// Types
import { SelectedExamCardType } from "src/services/exams/types";

// Constants
import {
  DEFAULT_STEREOPSIS_CARD,
  stereoTargetStyleOptions,
} from "src/constants/enums";

type StereopsisCardType = {
  label: string;
  selectedExamCard: SelectedExamCardType | undefined;
  updateExamCardProperty: (
    propertyName: keyof SelectedExamCardType,
    value: SelectedExamCardType[keyof SelectedExamCardType]
  ) => void;
  onRemoveCard: () => void;
};

const StereopsisCard = ({
  label,
  selectedExamCard,
  updateExamCardProperty,
  onRemoveCard,
}: StereopsisCardType) => {
  const { t } = useTranslation();

  const examCardInfo = selectedExamCard ?? DEFAULT_STEREOPSIS_CARD;

  const { stereoTargetStyle, optionalToggles } = examCardInfo;

  const selectedTargetStyle = stereoTargetStyle ?? stereoTargetStyleOptions[0];

  const targetStyleOptions = useMemo(
    () =>
      stereoTargetStyleOptions.map((option) => ({
        label: t(option),
        value: option,
      })),
    [t]
  );

  // Wide enough for the whole label, so a long one wraps to its own row
  // rather than being cut off.
  const buttonMinWidth = (buttonLabel: string) => `${buttonLabel.length + 2}ch`;

  const textStyle = {
    color: "#FFF",
    fontSize: "12px",
  };

  return (
    <ExamCardContainer>
      <ExamCardHeader isNew label={label} onRemoveCard={onRemoveCard} />
      <Grid
        item
        container
        display="flex"
        flexDirection="column"
        justifyContent="space-between"
        gap="8px"
        flexGrow={1}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            gap: "8px",
          }}
        >
          {/* Stereo Target Style */}
          <Grid item display="flex" flexDirection="column" gap="4px">
            <TitleWithTooltip
              title={t("Stereo Target Style")}
              tooltip={t(
                "Controls the shapes displayed to the patient in the exam."
              )}
              titleStyle={textStyle}
              tooltipStyle={textStyle}
            />
            <Grid container item display="flex" flexWrap="wrap" gap="4px">
              {targetStyleOptions.map((option) => (
                <Grid
                  item
                  xs
                  fontSize="12px"
                  minWidth={buttonMinWidth(option.label)}
                  key={option.value}
                >
                  <ExamCardButton
                    isSelected={selectedTargetStyle === option.value}
                    updateValue={() =>
                      updateExamCardProperty("stereoTargetStyle", option.value)
                    }
                    label={option.label}
                  />
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "8px",
          }}
        >
          <Divider color="#5B6D7A" />

          <ExamCardToggles
            optionalToggles={optionalToggles}
            updateExamCardProperty={updateExamCardProperty}
            showToggles={DEFAULT_STEREOPSIS_CARD.optionalToggles}
          />
        </Box>
      </Grid>
    </ExamCardContainer>
  );
};

export default StereopsisCard;
